'use client';
import React from 'react';

import { cn } from '@/lib/utils';

import { BlockHeader, BlockHeaderContent } from '@/cms/BlockHeaderContentDC';
import { BlockWrapper } from '@/cms/BlockWrapperDC';
import { BlockComponentModel } from '@/cms/types/blok-component';
import { CarouselRenderItemInfo } from '@/components/atoms/Carousel';
import { CarouselItem } from '@/components/atoms/Carousel/CarouselBlockItem';
import { NextImageProps } from '@/components/atoms/Image';
import ImageStrapi, {
  ImageStrapiProps,
} from '@/components/atoms/Image/ImageStrapi';
import {
  ToggleCMSProps,
  useCMSToggle,
} from '@/components/atoms/Toggle/useCMSToggle';
import { SECTION_NAMES } from '@/integrations/trackingScripts/constants';

import { Marquee } from '../../marquee/Marquee';

export interface CarouselBlockCardStrapiProps {
  cardUrl?: string;
  cardTitle?: string;
  icon: ImageStrapiProps;
  actionLabel?: string;
  renderImage: (props: Partial<NextImageProps>) => React.ReactNode;
}

export interface CarouselBlockStrapiProps {
  cards: CarouselBlockCardStrapiProps[];
  header: BlockHeader;
  toggle?: ToggleCMSProps;
  carousel_groups: {
    data: {
      attributes: {
        carouselElements: CarouselBlockCardStrapiProps[];
      };
    }[];
  };
}

export const CarouselBlockStrapi = ({
  block,
  ...rest
}: BlockComponentModel<CarouselBlockStrapiProps>) => {
  const { toggle, header, cards, carousel_groups } = block;

  const groupCards =
    carousel_groups?.data?.flatMap(
      (group) => group.attributes?.carouselElements || []
    ) ?? [];

  const rawCards = groupCards.length > 0 ? groupCards : cards;

  const {
    filteredToggleList,
    toggleNode: ToggleNode,
    ToggleActionButton,
  } = useCMSToggle<typeof rawCards>(rawCards, toggle);
  const selectedCards = filteredToggleList || rawCards;

  const renderCarouselItem = ({
    item,
    index,
  }: CarouselRenderItemInfo<CarouselBlockCardStrapiProps>) => (
    <CarouselItem
      key={index}
      {...item}
      renderImage={(props) => (
        <ImageStrapi
          inner={{
            ...props,
            classNames: {
              image: 'w-9 h-9',
            },
            'data-tracker-context': `carousel-item-${index}`,
          }}
          block={item.icon}
        />
      )}
    />
  );

  return (
    <BlockWrapper
      {...rest}
      contentFit='contained'
      data-tracker-section={SECTION_NAMES.CAROUSEL_BLOCK}
    >
      <div>
        <BlockHeaderContent
          {...header}
          topChildren={ToggleNode ? ToggleNode() : undefined}
          renderButtons={
            ToggleActionButton
              ? [
                  (props) => (
                    <ToggleActionButton
                      key='carousel-block-toggle-button'
                      inner={props}
                    />
                  ),
                ]
              : undefined
          }
          classNames={{
            children: cn(
              'md:text-left mx-auto',
              /**
               * Why 1192px?
               * Max design width = 1440
               * Max side overlay width = 124 x 2
               *
               * Effective max width to retain responsiveness = 1440 - 124 * 2 = 1192px;
               */
              'max-w-[1192px]'
            ),
          }}
        >
          <Marquee
            repeat={2}
            gap={24}
            classNames={{ inner: 'gap-x-6' }}
            data-tracker-section={SECTION_NAMES.CAROUSEL_BLOCK}
          >
            {selectedCards.map((item, index) =>
              renderCarouselItem({ item, index })
            )}
          </Marquee>
        </BlockHeaderContent>
      </div>
    </BlockWrapper>
  );
};
